import React from 'react';
import { Tag, TagType } from '@platform/witems';
import Media from './Media';
import AvaAvatar from './AvaAvatar';
import LabResultsCard from './LabResultsCard';
import MilestoneSummary from './MilestoneSummary';
import SpruceCallRow from './SpruceCallRow';
import {
  chatSender, initialsOf, fullNameOf, parseContentCard, milestoneOf, isSpruceRow, isSpruceEvent, isHiddenClinicLineRow,
  spruceAuthorFirstName, spruceTextCaption, AVA_NAME, AVA_ROLE,
} from '../logic/chatMessage';
import { labCardOf, isLabCardRow } from '../logic/labResults';
import { spruceThreadUrl } from '../logic/spruce';
import { Constants } from '../config/constants';
import { timeAgo } from '../utils/formatTime';

const CHANNEL_LABELS = {
  sms: 'SMS',
  email: 'Email',
  portal: 'Portal',
  both: 'SMS + Email',
  phone: 'Phone',
};

// Staff and Ava sit on the care-team side; the 6px corner points at the avatar.
export const BUBBLE_RADIUS = { right: '18px 18px 6px 18px', left: '18px 18px 18px 6px' };

function OpenInSpruce({ url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--wi-action-primary)' }}>
      Open in Spruce
    </a>
  );
}

const ORIGIN_TITLE = {
  ai: 'Written by AI',
  automated: 'Automated message',
};

function ContentCard({ card }) {
  return (
    <a
      href={card.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 8,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'var(--wi-background-secondary)',
        border: '1px solid var(--wi-divider)',
        textDecoration: 'none',
      }}
    >
      <span style={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: 10,
        background: 'var(--wi-background-info)',
        color: 'var(--wi-text-primary-info)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span className="body2" style={{ fontWeight: 600, color: 'var(--wi-text-primary)' }}>{card.title}</span>
        <span className="detail1" style={{ fontWeight: 600, color: 'var(--wi-text-primary-info)' }}>
          Read content <span aria-hidden="true">↗</span>
        </span>
      </span>
    </a>
  );
}

const READ_TICK = {
  seen: { label: 'Seen in the app', title: 'Seen in the app: the patient had the chat open with this message loaded' },
  not_seen: { label: 'Not seen in the app yet', title: 'Not seen in the app yet' },
};

// Two shapes, not just two colours: ✓ not seen, ✓✓ seen.
function ReadTick({ state }) {
  const tick = READ_TICK[state];
  if (!tick) return null;
  const seen = state === 'seen';
  return (
    <span title={tick.title} style={{ display: 'inline-flex', alignItems: 'center', color: seen ? 'var(--wi-status-info)' : 'var(--wi-text-secondary)' }}>
      <svg width={seen ? 18 : 12} height="12" viewBox={seen ? '0 0 30 24' : '0 0 24 24'} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {seen ? <><path d="M2 13l5 5L19 6" /><path d="M14 17l1 1L27 6" /></> : <path d="M5 13l4 4L19 7" />}
      </svg>
      <span className="sr-only">{tick.label}</span>
    </span>
  );
}

// `embedded` = the panel sits on --wi-background-secondary, so Ava's neutral bubble takes the other surface.
/** @param {{ entry: any, patient: any, embedded?: boolean, appSeen?: 'seen' | 'not_seen' }} props */
export default function ChatMessageBubble({
  entry, patient, embedded = false, appSeen,
}) {
  // A transcript is drawn under its call; an unknown clinic-line type is never drawn.
  if (isHiddenClinicLineRow(entry)) return null;
  const spruce = isSpruceRow(entry);
  // Our own clinic's org only (never the row's); null on dev and test.
  const threadUrl = spruce ? spruceThreadUrl(Constants.SPRUCE_ORG_ID, entry.payload_ref) : null;
  if (isSpruceEvent(entry)) return <SpruceCallRow entry={entry} threadUrl={threadUrl} transcript={entry.transcript ?? null} />;

  const sender = chatSender(entry);
  const isPatient = sender.kind === 'patient';
  const isAva = sender.kind === 'ava';
  const isRight = !isPatient;

  // Staff rows prefer the author captured at send time, so care-team members stay distinguishable.
  let senderName = fullNameOf(entry.author_firstname, entry.author_lastname) || 'You';
  if (isPatient) senderName = patient.firstname || 'Patient';
  else if (spruce) senderName = spruceAuthorFirstName(entry) || 'Clinic line';
  let initials = initialsOf(entry.author_firstname, entry.author_lastname, 'CT');
  if (isPatient) initials = initialsOf(patient.firstname, patient.lastname, '?');
  else if (spruce) initials = initialsOf(spruceAuthorFirstName(entry), null, 'CL');
  let channelLabel = entry.channel ? (CHANNEL_LABELS[entry.channel] || entry.channel) : null;
  // Only a clinic-line text reaches here: calls draw as a row and other types are hidden.
  if (spruce) channelLabel = 'Clinic line (SMS)';
  const caption = spruceTextCaption(entry);
  // A clinic-line text is the sender's words only: no card, no badge, no link.
  const labCard = spruce ? null : labCardOf(entry);
  const milestone = (spruce || labCard) ? null : milestoneOf(entry, patient.firstname);
  // A lab row that fails to parse stays plain text, never a link card: a link in a care team note is not clickable.
  const card = (spruce || labCard || milestone || isLabCardRow(entry)) ? null : parseContentCard(entry.body);

  let bubbleTone = { background: 'var(--wi-background-good)' };
  if (isPatient && spruce) bubbleTone = { background: 'var(--wi-background-info)', border: '1px solid var(--wi-status-info)' };
  else if (isPatient) bubbleTone = { background: 'var(--wi-background-info)' };
  else if (sender.urgent) bubbleTone = { background: 'var(--wi-background-bad)' };
  else if (isAva) {
    bubbleTone = {
      background: embedded ? 'var(--wi-background-primary)' : 'var(--wi-background-secondary)',
      border: '1px solid var(--wi-divider)',
    };
  }

  return (
    <div style={{
        display: 'flex',
        flexDirection: isRight ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 10,
        maxWidth: labCard ? '90%' : '80%',
        width: labCard ? '90%' : undefined,
        alignSelf: isRight ? 'flex-end' : 'flex-start',
      }}
    >
      {isAva ? <AvaAvatar size={32} /> : (
        <Media
          size={32}
          className={isPatient ? 'chat-avatar--patient' : 'chat-avatar--staff'}
          fallback={initials}
        />
      )}

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: isRight ? 'flex-end' : 'flex-start', gap: 4, minWidth: 0, flex: labCard ? 1 : undefined,
      }}
      >
        <div className="detail1" style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: isRight ? 'flex-end' : 'flex-start',
          gap: 6,
          color: 'var(--wi-text-secondary)',
        }}>
          {sender.urgent && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600, color: 'var(--wi-text-primary-bad)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3 2 21h20L12 3z" /><path d="M12 10v5" /><path d="M12 18h.01" />
              </svg>
              Urgent
            </span>
          )}
          {isAva ? (
            <span title={ORIGIN_TITLE[sender.origin]}>
              <span style={{ fontWeight: 600, color: 'var(--wi-text-primary)' }}>{AVA_NAME}</span> – {AVA_ROLE}
            </span>
          ) : (
            <span style={{ fontWeight: 600, color: 'var(--wi-text-primary)' }}>{senderName}</span>
          )}
          <span aria-hidden="true">·</span>
          <span>{timeAgo(entry.sent_at)}</span>
          {channelLabel && (
            <>
              <span aria-hidden="true">·</span>
              <span>{channelLabel}</span>
            </>
          )}
          <ReadTick state={appSeen} />
        </div>

        {caption && (
          <div className="detail1" style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, justifyContent: isRight ? 'flex-end' : 'flex-start', color: 'var(--wi-text-secondary)',
          }}
          >
            <span>{caption}</span>
            {isPatient && <Tag type={TagType.MODERATE} label="sender unverified" />}
            {threadUrl && <OpenInSpruce url={threadUrl} />}
          </div>
        )}

        <div style={{
          padding: labCard ? '14px 16px' : '10px 14px',
          borderRadius: isRight ? BUBBLE_RADIUS.right : BUBBLE_RADIUS.left,
          minWidth: 0,
          alignSelf: labCard ? 'stretch' : undefined,
          ...bubbleTone,
        }}>
          {labCard ? <LabResultsCard model={labCard} embedded={embedded} /> : milestone ? (
            <MilestoneSummary model={milestone} ping={entry.ping} body={entry.body} patientFirstName={patient.firstname} />
          ) : (
            <>
              <p className="body2" style={{
                margin: 0,
                lineHeight: 1.5,
                color: 'var(--wi-text-primary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {card ? card.text : entry.body}
              </p>
              {card && <ContentCard card={card} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
